import { supabase } from '@/integrations/supabase/client';

/**
 * Get the effective organization ID for a user
 * - Returns admin's user_id if user is a team member
 * - Returns user's own ID if solo or admin
 * 
 * @param userId The user's ID (from useAuth)
 * @param userEmail The user's email (from useAuth)
 * @returns Promise<string> - Always returns a valid organization ID
 */
export const getOrganizationId = async (userId: string, userEmail: string): Promise<string> => {
  try {
    const { data: teamMember } = await supabase
      .from('team_members')
      .select('organization_id')
      .eq('email', userEmail.toLowerCase())
      .maybeSingle();  // Use maybeSingle instead of single
    
    // If user is in team_members, use the admin's organization_id
    if (teamMember) {
      return teamMember.organization_id;
    }
    
    return userId;
    
  } catch (error) {
    console.debug('Error getting organization ID, using user ID as fallback:', error);
    return userId;
  }
};

/**
 * Check if a user is an organization (has company_name in profile)
 * Useful for showing/hiding team features
 */
export const isOrganization = async (userId: string): Promise<boolean> => {
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('company_name')
      .eq('user_id', userId)
      .single();
    
    return !!(profile?.company_name);
  } catch (error) {
    return false;
  }
};

/**
 * Check if a user is a team member (non-admin)
 */
export const isTeamMember = async (userEmail: string): Promise<boolean> => {
  try {
    const { data: teamMember } = await supabase
      .from('team_members')
      .select('role')
      .eq('email', userEmail.toLowerCase())
      .maybeSingle();
    
    // User is a team member if they exist in team_members AND role is not 'admin'
    return !!(teamMember && teamMember.role !== 'admin');
  } catch (error) {
    return false;
  }
};

/**
 * Get the organization name (company_name) for display
 * - For team members: returns their org's company_name
 * - For org admins: returns their own company_name
 * - For individuals: returns null
 * 
 * @param organizationId The resolved organization ID
 * @returns Promise<string | null> - Company name or null
 */
export const getOrganizationName = async (organizationId: string): Promise<string | null> => {
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('company_name')
      .eq('user_id', organizationId)
      .maybeSingle();
    
    return profile?.company_name || null;
  } catch (error) {
    console.debug('Error getting organization name:', error);
    return null;
  }
};